<template>
	<div class="goal card drop-down-hover" :class="{ 'bg-gray-100' : goal.status != 1 }" data-test="goalItem">
		<div class="card-header d-flex align-items-center py-25" :class="goal.goal_type">
			<span v-if="goal.goal_type == 'charge'" class="flex-grow-1">Charges Goal <i class="fal fa-wallet"></i></span>
            <span v-else-if="goal.goal_type == 'referral'" class="flex-grow-1">Referrals Goal <i class="fal fa-user-injured"></i></span>
            <span v-else class="flex-grow-1">Check-ins Goal <i class="fal fa-check-circle"></i></span>

            <span v-if="goal.can.update || goal.can.delete" class="drop-down text-transform-none">
                <a href="#" role="button" id="dropdownMenuLink" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"><i class="far fa-ellipsis-h text-xl"></i></a>
                <div class="dropdown-menu dropdown-menu-right py-0" aria-labelledby="dropdownMenuLink" data-test="goalContextMenu">
                    <span class="dropdown-item" v-if="goal.can.update" @click="startEditing()" data-test="editGoal">Edit Goal</span>
                    <span class="dropdown-item" v-if="goal.can.update && goal.status === 1" @click="rebuildGoalHistory()" data-test="rebuildGoalHistory">Rebuild History</span>
                    <span class="dropdown-item" v-if="goal.can.update && goal.status === 2" @click="updateGoalStatus(1)" data-test="setGoalActive">Set active</span>
                    <span class="dropdown-item" v-if="goal.can.update && goal.status === 1" @click="updateGoalStatus(2)" data-test="setGoalInactive">Set inactive</span>
                    <span class="dropdown-item" v-if="goal.can.delete" @click="triggerDeleteGoal()" data-test="deleteGoal">Delete</span>
                </div>
            </span>           
		</div>

		<div class="card-body" style="position:relative;">
			<div v-if="state === 'editing'">
                <transition name="fade">
                    <div v-if="changed_attbs || changed_lines || changed_specs" class="rounded bg-yellow-100 text-yellow-800 px-35 py-2 mb-3">
                        Editing these goal attributes will not change the goal history unless "Rebuild History" is run.  
                    </div>
                </transition>
                <alert-errors :form="form">There were some problems with your input.</alert-errors>
                <alert-success :form="form">Your goal was saved.</alert-success>
                <form @submit.prevent="saveGoal" @keydown="form.onKeydown($event)" @change="form.onKeydown($event)">
                    <div class="form-group">
                        <label class="mb-0">Title</label>
                        <input v-model="form.title" class="form-control" data-test="editGoalTitle">
                        <has-error :form="form" field="title" />
                    </div>

                    <div v-if="can_set_any_user_goal && form.scope === 'user'" class="form-group">
                        <label class="mb-0">User</label>
                        <v-select                    
                            label="display_name"                            
                            :options="options.user_ids"
                            :reduce="display_name => display_name.id"
                            v-model="form.user_id"
                            data-test="editGoalUser">
                            <template v-slot:option="option">
                                <div class="d-flex align-items-center py-1">
                                    <div class="rounded-circle circle-cropped avatar-sm mr-2 text-center">
                                        <img :src="option.photo_url" class="img-fluid">
                                    </div>
                                    <div class="flex-grow-1">{{ option.display_name }}</div>
                                </div>
                            </template>
                        </v-select>
                        <has-error :form="form" field="user_id" />
                    </div>

                    <div class="d-flex mb-3">
                        <div class="w-50 mr-2">
                            <label class="mb-0">Start Date</label>
                            <date-picker
                                readonly="true"
                                class="date-picker"
                                v-model="form.start_date"
                                :config="config.datePickerOptions"
                                data-test="editGoalStart" />
                        </div>
                        <div class="flex-fill">
                            <label class="mb-0">End Date <span class="text-gray-500"> - Optional</span></label>
                            <date-picker
                                readonly="true"
                                class="date-picker"
                                v-model="form.end_date"
                                :config="config.datePickerOptions"
                                data-test="editGoalEnd" />
                         </div> 
                    </div>

                    <div class="d-flex mb-3">
                        <div class="w-50 mr-2">
                            <label class="mb-0">
                                {{ goal.frequency == 'day' ? 'Daily ' : goal.frequency == 'week' ? 'Weekly ' : 'Monthly ' }} 
                                {{ goal.goal_type == 'referral' ? 'referrals' : goal.goal_type == 'charge' ? 'charges' : 'check-ins' }} target
                            </label>
                            <input v-model="form.target" class="form-control" placeholder="Num. Check-ins" required data-test="editGoalTarget">
                        </div>

                        <div v-show="form.goal_type === 'checkin_type'" class="flex-fill">
                            <label class="mb-0">Check-ins of Type</label>
                            <v-select                    
                                label="label"
                                :clearable="false"
                                :options="options.checkin_types"
                                :reduce="label => label.id"
                                v-model="form.checkin_type_id"
                                data-test="editGoalCheckinType" />
                            <has-error :form="form" field="checkin_type_id" />
                        </div>

                        <div v-show="form.goal_type === 'service_line'" class="flex-fill">
                            <label class="mb-0">Promoting Service Lines</label>
                            <v-select
                                label="label"
                                :clearable="false"
                                :options="options.service_lines"
                                :reduce="label => label.id"
                                v-model="form.service_lines"
                                data-test="editGoalServiceLines" />
                            <has-error :form="form" field="service_lines" />
                        </div>

                        <div v-show="form.goal_type === 'specialty'" class="flex-fill">
                            <label class="mb-0">Outreach to Specialties</label>
                            <v-select
                                label="label"
                                :clearable="false"
                                :options="options.specialties"
                                :reduce="label => label.id"
                                v-model="form.specialties"
                                data-test="editGoalSpecialties" />
                            <has-error :form="form" field="specialties" />
                        </div>
                    </div>

                    <div class="d-flex justify-content-between mt-3">
                        <button type="button" class="btn btn-ghost-secondary" @click="cancelEditing()">Cancel</button>                        
                        <button type="button" class="btn btn-primary" @click="updateGoal()" data-test="saveEditGoalButton">Save</button>
                    </div>  
                </form>

			</div>

			<div v-else>
                <div v-if="goal.active_goal_segment && goal.active_goal_segment.matching_checkins >= goal.target" class="completed-badge-wrapper">
                    <div class="completed-badge bg-green-100 text-green-600">
                        <i class="fad fa-trophy-alt text-3xl"></i>
                    </div>
                </div>

                <div class="d-flex align-items-center mb-3">
                    <div class="rounded-circle circle-cropped avatar-sm mr-2 text-center bg-gray-100 text-gray-500">
                        <img v-if="goal.scope === 'user'" :src="goal.user.photo_url" class="img-fluid" v-tooltip.bottom="goal.user.display_name">
                        <i v-else class="fal fa-hospital"></i>
                    </div>
                    <div class="flex-grow-1 text-blue-600 text-lg font-weight-bold">{{ goal.title }}</div>
                </div>

                <div class="mb-3 d-flex">
                    <div class="flex-grow-1">
                        <template v-if="goal.active_goal_segment">
                            <div class="mb-2 text-sm text-uppercase text-gray-700">
                                {{ moment(goal.active_goal_segment.start_date).format('MMM D') }} - {{ moment(goal.active_goal_segment.end_date).format('MMM D') }}
                            </div>
                            <span class="text-4xl leading-tight">{{ goal.goal_type == 'charge' ? numeral(goal.active_goal_segment.matching_checkins).format('$0a') : goal.active_goal_segment.matching_checkins }}</span>
                            <span class="text-2xl text-gray-500"> / {{ goal.goal_type == 'charge' ? numeral(goal.target).format('$0a') : goal.target }}</span>
                        </template>

                        <template v-else>
                            <div class="mb-2 text-sm text-uppercase text-gray-700">
                                Progress not available
                            </div>
                            <span class="text-4xl leading-tight">--</span>
                            <span class="text-2xl text-gray-500"> / {{ goal.target }}</span>
                        </template>

                        <div class="text-gray-500">
                            {{ goal.frequency == 'day' ? 'Daily ' : goal.frequency == 'week' ? 'Weekly ' : 'Monthly ' }}
                            <span v-if="goal.goal_type == 'referral'" v-html="'Patient Referrals'" />
                            <span v-if="goal.goal_type == 'charge'" v-html="'Generated Charges'" />
                            <span v-if="goal.goal_type == 'checkin'" v-html="'Check-ins'" />
                            <span v-if="goal.goal_type == 'service_line'" v-html="'Check-ins promoting ' + goal.service_lines[0].label" />
                            <span v-if="goal.goal_type == 'specialty'" v-html="'Check-ins with ' + goal.specialties[0].label" />
                            <span v-if="goal.goal_type == 'checkin_type'" v-html="'Check-ins of type ' + goal.checkin_type ? goal.checkin_type.label : '(unknown)'" />
                        </div>
                    </div>
                    
                    <goal-item-donut v-if="goal.active_goal_segment" :segment="goal.active_goal_segment" class="mx-2" />
                </div>    
                <div class="mt-4 text-sm text-uppercase text-gray-700">
                    <span @click="toggleHistory()" class="cursor-pointer" data-test="toggleHistory">History</span>
                    <i class="far" :class="showHistory == false ? 'fa-angle-right' : 'fa-angle-down'"></i>
                    <line-chart
                        :class="{ 'pending' : pending }"
                        class="mt-2"
                        v-if="showHistory"
                        :chart-data="goalSegmentHistory"
                        :options="optionsChart"
                        :height="85"
                    />                    
                </div>            
			</div>            
		</div>

        <div class="card-footer">
            <i class="fal fa-calendar-day mr-1"></i> {{ goal.start_date_display }} to {{ goal.end_date_display }}
        </div>

	</div>
</template>

<script>

    import Vue from 'vue';
    import { mapState } from 'vuex';
    import { Form, HasError, AlertError, AlertErrors, AlertSuccess } from 'vform';
    import GoalItemDonut from './GoalItemDonut'
    import LineChart from '../helpers/LineChart.js';

    export default {
        
        components: {
            Form,
            HasError,
            AlertError,
            AlertErrors,
            AlertSuccess,
            GoalItemDonut,
            LineChart,
        },

    	props: {
            goal: Object,
            can_set_facility_goal: Boolean,
            can_set_any_user_goal: Boolean,
            can_set_own_user_goal: Boolean,
        },

    	data() {
    		return {
    			state: 'default',

                changed_attbs: false,
                changed_lines: false,
                changed_specs: false,

    			datePickerOptions: {
    				format: 'MMM D, YYYY',
    			},
    			form: {},
                
                goalSegmentHistory: {},

                showHistory: false,
                pending: false,

                optionsChart: {
                    height: '85px',
                    responsive: true,                
                    maintainAspectRatio: false,
                    title: {
                        display: false,
                    },                
                    legend: {
                        display: false
                    },                 
                    animation: {
                        duration: 0,
                    },                
                    scales: {
                        xAxes: [{
                            type: 'time',
                            time: {
                                unit: 'month',
                                displayFormats: {
                                    month: 'MMM'
                                }
                            },                          
                            distribution: 'linear',
                            gridLines: {
                                drawTicks: false,
                                drawOnChartArea: false,
                            },                      
                            ticks: {
                                display: true,
                                fontSize: 10,
                                fontColor: '#A0A3A6',
                                padding: 6,
                            }
                        }],
                        yAxes: [{
                            display: true,
                            gridLines: {
                                display:true,
                                drawOnChartArea: false,
                                tickMarkLength: 4,
                                zeroLineColor: 'rgba(0,0,0,0)',
                            },
                            ticks: {
                                min: 0,
                                fontSize: 10,
                                fontColor: '#A0A3A6',
                                padding: 6,
                                display:true,
                                maxTicksLimit: 4,
                                callback: (value, index, values) => {
                                    if (this.goal.goal_type == 'charge') {
                                        return numeral(value).format('$0a');
                                    }
                                    return value;
                                }
                            }  
                        }],
                    },

                    tooltips: {
                        callbacks: {
                            title: function(tooltipItem, data) {

                                return moment(tooltipItem[0].label).format('MMM D, YYYY');
                            }
                        }
                    },

                },

    		}
    	},

    	methods: {
            cancelEditing() {
                this.state = 'default';
            },

            fetchHistory() {
                axios.get('/api/goals/' + this.goal.id).then(({data}) => {
                    this.goalSegmentHistory = {
                        datasets: [{
                            label: 'Actual',
                            backgroundColor: '#FAECFB',
                            borderColor: '#836085',
                            borderWidth: 1.5,
                            radius: 2.5,
                            data: data.goal_segments.map(segment => ({ t: segment.start_date, y: segment.actual })),
                            lineTension: .15,
                        },
                        {
                            label: 'Target',
                            borderDash: [2,3],
                            borderColor: '#EE2D42',
                            backgroundColor: 'rgba(0,0,0,0)',
                            borderWidth: 1,
                            radius: 0,
                            data: data.goal_segments.map(segment => ({ t: segment.start_date, y: segment.target }))
                        }]                  
                    }
                    this.pending = false;
                });
            },

            rebuildGoalHistory() {
                Swal.fire({
                      title: 'Are you sure?',
                      text: 'Existing goal history will be erased and re-built based on the current goal parameters. This may take a moment.',
                      icon: 'warning',
                      showCancelButton: true,
                      confirmButtonText: 'Rebuild',
                      cancelButtonText: 'Cancel',
                      animation: false,
                    }).then((result) => {
                        if (result.value) {
                            this.pending = true;
                            axios.put('/api/goals/' + this.goal.id + '/history', this.goal.id).then(() => {
                                this.fetchHistory();                               
                            })
                        }
                    }
                );
            },

    		startEditing() {
    			this.state = 'editing';
    			this.form = new Form({
                    title: this.goal.title,
                    scope: this.goal.scope,
                    user_id: this.goal.user_id,
                    client_id: this.goal.client_id,
                    start_date: this.goal.start_date_display,
                    end_date: this.goal.end_date_display == 'indefinite' ? null : this.goal.end_date_display,
                    target: this.goal.target,
                    frequency: this.goal.frequency,
                    goal_type: this.goal.goal_type,
                    checkin_type_id: this.goal.checkin_type ? this.goal.checkin_type.id : null,
                    service_lines: this.goal.service_lines.length ? this.goal.service_lines[0].id : null,
                    specialties: this.goal.specialties.length ? this.goal.specialties[0].id : null,                    
                });
    		},

            updateGoal() {
                this.form.put('/api/goals/' + this.goal.id).then(({ data }) => { 
                	this.$emit('update_goal', data);
                	this.state = 'default';
                })
            },

            toggleHistory() {
                this.showHistory = !this.showHistory;
                if (this.showHistory) {
                    this.pending = true;
                    this.fetchHistory();
                }
            },

            triggerDeleteGoal() {
                this.$emit('delete_goal', {
                    'id': this.goal.id,
                });
            },

            updateGoalStatus(status) {
            	let statusData = {
            		'id': this.goal.id,
            		'status': status,
            	};
                axios.put('/api/goals/' + this.goal.id + '/status', statusData).then(({ data }) => { 
                    this.$emit('update_goal_status', statusData);
                })           	
            },

    	},

        computed: mapState(['config', 'options']),

        watch: {
            form: {
                deep: true,           
                handler() {
                    if (
                        this.goal.user_id != this.form.user_id || 
                        this.goal.target != this.form.target || 
                        this.goal.checkin_type_id != this.form.checkin_type_id ||
                        moment(this.goal.start_date).format('MMM D, YYYY') != this.form.start_date ||
                        moment(this.goal.end_date).format('MMM D, YYYY') != moment(this.form.end_date).format('MMM D, YYYY')
                    ) {
                        this.changed_attbs = true;
                    } else {
                        this.changed_attbs = false;
                    }

                    if (this.goal.service_lines.length && (this.goal.service_lines[0].id != this.form.service_lines)) {
                        this.changed_lines = true;
                    } else {
                        this.changed_lines = false;
                    }

                    if (this.goal.specialties.length && (this.goal.specialties[0].id != this.form.specialties)) {
                        this.changed_specs = true;
                    } else {
                        this.changed_specs = false;
                    }                    
                }
            }
        }

    }
</script>

<style scoped>


</style>